/**
 * RunningComparison page
 *
 * Handles data fetching and the share dialog.
 * All tab UI is delegated to RunningComparisonView.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { tokens } from '../../theme';
import { compareRunningWorkout, getComparisonById } from '../../api/runningApi';

import RunningComparisonView from '../../components/running/RunningComparisonView';
import ShareComparisonDialog from '../../components/running/ShareComparisonDialog';
import ComparisonHeader from '../../components/running/comparison/ComparisonHeader';

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function LoadingSkeleton({ onCancel }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  return (
    <Box m="20px">
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Skeleton variant="text" width={200} height={36} sx={{ bgcolor: colors.grey[800] }} />
        <Button variant="text" onClick={onCancel} sx={{ color: colors.grey[400] }}>
          {t('cancel', 'Cancel')}
        </Button>
      </Box>
      <Box display="flex" gap={2} mb={3}>
        <Skeleton variant="rectangular" width="48%" height={120} sx={{ borderRadius: 2, bgcolor: colors.grey[800] }} />
        <Box display="flex" alignItems="center">
          <Typography variant="h4" color={colors.grey[500]}>{t('vs', 'vs')}</Typography>
        </Box>
        <Skeleton variant="rectangular" width="48%" height={120} sx={{ borderRadius: 2, bgcolor: colors.grey[800] }} />
      </Box>
      <Box display="flex" gap={2} mb={3}>
        {[t('raceDynamics', 'Race Dynamics'), t('mapGhostRace', 'Map & Ghost Race'), t('climbs', 'Climbs'), t('descents', 'Descents'), t('summary', 'Summary')].map((label) => (
          <Skeleton key={label} variant="text" width={120} height={40} sx={{ bgcolor: colors.grey[800] }} />
        ))}
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        <CircularProgress size={20} />
        <Typography color={colors.grey[400]}>{t('analyzingComparison', 'Analyzing comparison workout...')}</Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function RunningComparison() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    baseSessionId,
    source,
    comparisonId,
    snapBToGpx = false,
    // null = let the backend use B's own stored setting. A default of true
    // re-analysed B under a setting its own page never used.
    applyGpsCorrectionsBTo = null,
    workoutA = null,
    labelA = null,
  } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Navigation guard
  useEffect(() => {
    if (!comparisonId && (!baseSessionId || !source)) {
      navigate('/running', { replace: true });
    }
  }, [comparisonId, baseSessionId, source, navigate]);

  // Fetch comparison
  useEffect(() => {
    if (!comparisonId && (!baseSessionId || !source)) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    if (comparisonId) {
      getComparisonById(comparisonId)
        .then((result) => {
          if (cancelled) return;
          // A snapshot from the old pipeline would quietly show the numbers the
          // user is trying to verify have changed. Rebuild it when the backend
          // hands back enough to do so, and fall back to showing the snapshot
          // when it does not (B uploaded once and never saved as a file).
          if (result?.stale && result.session_a_id && result.comparison_file_id) {
            return compareRunningWorkout(
              result.session_a_id,
              { type: 'myfile', fileId: result.comparison_file_id },
            ).then((fresh) => {
              if (!cancelled) { setComparisonResult(fresh); setLoading(false); }
            });
          }
          setComparisonResult(result);
          setLoading(false);
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err?.response?.data?.detail || err?.message || 'Failed to load past comparison');
            setLoading(false);
          }
        });
    } else {
      const applyGpsCorrectionsAValue = workoutA?.parameters?.apply_gps_corrections ?? true;
      const snapAToGpxValue = workoutA?.parameters?.snap_to_gpx ?? false;

      compareRunningWorkout(
        baseSessionId,
        source,
        snapBToGpx,
        applyGpsCorrectionsBTo,
        snapAToGpxValue,
        applyGpsCorrectionsAValue
      )
        .then((result) => {
          if (!cancelled) { setComparisonResult(result); setLoading(false); }
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err?.response?.data?.detail || err?.message || 'Unknown error');
            setLoading(false);
          }
        });
    }

    return () => { cancelled = true; };
  }, [comparisonId, baseSessionId, source, snapBToGpx]);

  const handleBack = useCallback(() => {
    if (workoutA) navigate('/running/analysis', { state: { workoutData: workoutA }, replace: true });
    else navigate(-1);
  }, [workoutA, navigate]);

  const analysisA = comparisonResult?.analysis_a;
  const analysisB = comparisonResult?.analysis_b;
  const alignment = comparisonResult?.alignment;
  const checkpoints = comparisonResult?.checkpoints;
  const labelAFromResult = comparisonResult?.a_filename;
  const labelBFromResult = comparisonResult?.b_filename;
  const descriptionAFromResult = comparisonResult?.a_description;
  const descriptionBFromResult = comparisonResult?.b_description;
  const resampled = alignment?.resampled ?? [];

  if (loading) return <LoadingSkeleton onCancel={handleBack} />;

  if (error) {
    return (
      <Box m="20px">
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ color: colors.grey[300] }}>
          {t('backToAnalysis', 'Back to Analysis')}
        </Button>
      </Box>
    );
  }

  const headerSection = (
    <>
      {/* Back + Share Comparison */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ color: colors.grey[400], textTransform: 'none', fontSize: '0.85rem' }}
        >
          {t('backToAnalysis', 'Back to Analysis')}
        </Button>
        {comparisonResult && (
          <Button
            variant="outlined"
            onClick={() => setShareDialogOpen(true)}
            sx={{
              color: colors.greenAccent[300],
              borderColor: colors.greenAccent[600],
              fontSize: isMobile ? '0.75rem' : '0.85rem',
              '&:hover': { borderColor: colors.greenAccent[400], backgroundColor: colors.greenAccent[900] },
            }}
          >
            {t('shareComparison', 'Share Comparison')}
          </Button>
        )}
      </Box>

      {/* ComparisonHeader: identity cards + controls */}
      {comparisonResult && (
        <ComparisonHeader
          analysisA={analysisA}
          analysisB={analysisB}
          alignment={alignment}
          checkpoints={checkpoints}
          labelA={labelAFromResult || labelA}
          labelB={labelBFromResult}
          descriptionA={descriptionAFromResult}
          descriptionB={descriptionBFromResult}
          sourceA={comparisonResult?.a_source}
          sourceB={comparisonResult?.b_source}
          deviceNameA={comparisonResult?.a_device_name}
          deviceNameB={comparisonResult?.b_device_name}
          bIsPrediction={!!analysisB?.is_prediction}
        />
      )}

      {/* Snap to GPX notices */}
      {analysisA?.snap_to_gpx_result?.skipped && (
        <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
          <strong>{t('runnerALabel', 'Runner A')}:</strong> {t('snapToGpxSkipped', { on_course: analysisA.snap_to_gpx_result.on_course_points || 0, total: analysisA.snap_to_gpx_result.total_points, defaultValue: `Snap to GPX was skipped: only {{on_course}}/{{total}} points were detected on-course (<10% threshold).` })}
        </Alert>
      )}
      {analysisB?.snap_to_gpx_result?.skipped && (
        <Alert severity="warning" sx={{ mt: analysisA?.snap_to_gpx_result?.skipped ? 1 : 2, mb: 1 }}>
          <strong>{t('runnerBLabel', 'Runner B')}:</strong> {t('snapToGpxSkipped', { on_course: analysisB.snap_to_gpx_result.on_course_points || 0, total: analysisB.snap_to_gpx_result.total_points, defaultValue: `Snap to GPX was skipped: only {{on_course}}/{{total}} points were detected on-course (<10% threshold).` })}
        </Alert>
      )}
    </>
  );

  return (
    <>
      <RunningComparisonView
        analysisA={analysisA}
        analysisB={analysisB}
        alignment={alignment}
        resampled={resampled}
        checkpoints={checkpoints}
        headerSection={headerSection}
        extraDialogs={
          comparisonResult && (
            <ShareComparisonDialog
              open={shareDialogOpen}
              onClose={() => setShareDialogOpen(false)}
              comparisonData={{
                base_session_id: baseSessionId,
                labelA,
                labelB: comparisonResult?.b_filename,
                snap_b_to_gpx: snapBToGpx,
                ...(source?.type === 'upload' && { comparison_fit_file: source.file }),
                ...(source?.type === 'myfile' && { comparison_file_id: source.fileId }),
                ...(source?.type === 'community' && { comparison_shared_id: source.sharedId }),
                ...(source?.type === 'prediction' && { comparison_prediction_id: source.predictionId }),
              }}
            />
          )
        }
      />
    </>
  );
}
